import { getEventModelName } from "@/entities/data-model";
import {
    EventAssertion,
    type EventFieldsGroup,
    type FormMetadataResponse,
    GroupType,
    type HierarchicalFieldGroup,
    ResponseItemType,
} from "@/entities/form-metadata";

export interface EventExactnessConfig {
    /** Set of event keys (e.g. "SelfBirth") where Year has showExactness: false */
    yearDisabled: Set<string>;
    /** Set of event keys where Place has showExactness: false */
    placeDisabled: Set<string>;
}

/** Collects, per event key, which event fields withhold exactness (`showExactness: false`) in the form metadata. */
export const getEventExactnessConfig = (formMetadata: FormMetadataResponse): EventExactnessConfig => {
    const yearDisabled = new Set<string>();
    const placeDisabled = new Set<string>();

    for (const entry of formMetadata.fieldGroupsAndFeatureContainers) {
        if (entry.type !== ResponseItemType.FieldGroup) continue;

        if (entry.groupType === GroupType.Event) {
            const eventGroup = entry as EventFieldsGroup;
            for (const fieldset of eventGroup.fieldsets) {
                for (const field of fieldset.fields) {
                    if (field.features.showExactness !== false) continue;
                    // Use the field's own identity, not the fieldset's: on a mixed row a
                    // withheld place exactness belongs to the event the place field names, not to the row's label.
                    const eventKey = getEventModelName(field);
                    if (!eventKey) continue;
                    if (field.assertion === EventAssertion.Year) {
                        yearDisabled.add(eventKey);
                    }
                    if (field.assertion === EventAssertion.Place) {
                        placeDisabled.add(eventKey);
                    }
                }
            }
        }

        if (entry.groupType === GroupType.Hierarchical) {
            // Hierarchical groups only expose place fields (e.g. country → state → city),
            // so they can only ever contribute to placeDisabled — never yearDisabled.
            const hierarchicalGroup = entry as HierarchicalFieldGroup;
            for (const hierarchy of hierarchicalGroup.hierarchies) {
                const eventKey = getEventModelName({
                    eventType: hierarchy.eventType,
                    relation: hierarchy.relation,
                });
                if (!eventKey) continue;
                for (const field of hierarchy.fields) {
                    if (field.features.showExactness === false) {
                        placeDisabled.add(eventKey);
                    }
                }
            }
        }
    }

    return { yearDisabled, placeDisabled };
};
